// Messaging type aliases for cleaner imports

// Conversation types
import type { messaging_ConversationResponse } from '@/api';
// Value import (deep, so a `vi.mock('@/api')` cannot blank the enum): the
// broadcast target type reuses the generated enum below.
import { messaging_CreateBroadcastRequest } from '@/api/models/messaging_CreateBroadcastRequest';

export type {
  messaging_ConversationDetailResponse as ConversationDetail,
  messaging_ConversationListResponse as ConversationListResponse,
  messaging_CreateConversationRequest as CreateConversationRequest,
  messaging_ArchiveConversationRequest as ArchiveConversationRequest,
  messaging_MarkAsReadRequest as MarkAsReadRequest,
} from '@/api';

// Extended Conversation type with optional unread_count field
// This field may be populated by the backend in the future
export type Conversation = messaging_ConversationResponse & {
  unread_count?: number;
};

// Message types
export type {
  messaging_MessageResponse as Message,
  messaging_MessageWithSenderResponse as MessageWithSender,
  messaging_SendMessageRequest as SendMessageRequest,
  messaging_EditMessageRequest as EditMessageRequest,
  messaging_UnreadCountResponse as UnreadCountResponse,
  messaging_ReadReceiptResponse as ReadReceipt,
} from '@/api';

// Attachment types
export type {
  messaging_AttachmentResponse as Attachment,
  messaging_AttachmentUploadRequest as AttachmentUploadRequest,
  messaging_ConfirmAttachmentRequest as ConfirmAttachmentRequest,
} from '@/api';

// Messaging children (the family recipients of a new conversation)
export type {
  messaging_MessagingChildrenResponse as MessagingChildrenResponse,
  messaging_MessagingChildResponse as MessagingChild,
  messaging_MessagingParentResponse as MessagingParent,
} from '@/api';

// Reply template types
export type {
  messaging_ReplyTemplateResponse as ReplyTemplate,
  messaging_ReplyTemplateListResponse as ReplyTemplateListResponse,
  messaging_CreateReplyTemplateRequest as CreateReplyTemplateRequest,
  messaging_UpdateReplyTemplateRequest as UpdateReplyTemplateRequest,
} from '@/api';

// Shared types
export type { models_SenderType as SenderType } from '@/api';
export type { models_ConversationType as ConversationType } from '@/api';

// Re-export with original names for backward compatibility
export type { messaging_ConversationResponse } from '@/api';
export type { messaging_ConversationDetailResponse } from '@/api';
export type { messaging_MessageResponse } from '@/api';
export type { messaging_MessageWithSenderResponse } from '@/api';
export type { messaging_ReplyTemplateResponse } from '@/api';

// Broadcast types (not generated - custom endpoint).
// `target_type` reuses the generated (nominal) enum rather than re-declaring the
// literals, so this shape stays assignable to `messaging_CreateBroadcastRequest`.
export interface CreateBroadcastRequest {
  target_type: messaging_CreateBroadcastRequest.target_type;
  target_ids: string[];
  subject?: string;
  message: string;
  allow_comments: boolean;
}

export interface CreateBroadcastResponse {
  created: number;
  conversations: messaging_ConversationResponse[];
}

// Query key parameter types
export interface ConversationListParams {
  userType?: 'contact' | 'employee';
  status?: 'active' | 'archived';
  groupIds?: string[];
  childcareIds?: string[];
  search?: string; // Search query for subject, message content, and sender names
  limit?: number;
  offset?: number;
  /** family = parent correspondence, staff = staff-internal threads. Narrows the view only. */
  audience?: 'family' | 'staff';
  /** Also match threads addressed to the office (always on for admins). Narrows the view only. */
  includeOffice?: boolean;
}

export interface ReplyTemplateListParams {
  limit?: number;
  offset?: number;
  /** Server-side term matched against template name and content. */
  search?: string;
}

// Query key factories
export const conversationKeys = {
  all: ['conversations'] as const,
  lists: () => [...conversationKeys.all, 'list'] as const,
  list: (params: ConversationListParams) =>
    [...conversationKeys.lists(), params] as const,
  details: () => [...conversationKeys.all, 'detail'] as const,
  detail: (id: string) => [...conversationKeys.details(), id] as const,
};

export const messageKeys = {
  all: ['messages'] as const,
  byConversation: (conversationId: string) =>
    [...messageKeys.all, 'conversation', conversationId] as const,
  unreadCount: (userType: string) =>
    [...messageKeys.all, 'unread', userType] as const,
};

export const replyTemplateKeys = {
  all: ['replyTemplates'] as const,
  lists: () => [...replyTemplateKeys.all, 'list'] as const,
  list: (params: ReplyTemplateListParams) =>
    [...replyTemplateKeys.lists(), params] as const,
};
